/**
 * Etapa 3 do wizard - Regras da Cobranca.
 *
 * Secoes:
 *  1. Limites de valores para a cobranca (min/max + Acrescimo opcional)
 *  2. Liberacao de novas cobrancas simultaneas (limite rotativo)
 *  3. Renovacao automatica (gates)
 *
 * "Dias da semana para cobrar" foi movido para a etapa anterior
 * (Step3Installments — "Plano de parcelas e prazos").
 *
 * Tipo de multa por atraso esta na Etapa 5 (Multas).
 */

import { useTranslation } from "react-i18next";
import type { Dispatch, SetStateAction } from "react";
import { Field, MoneyInput, NumberInput, PercentInput } from "../components/Field";
import { InfoCallout, SectionCard } from "../components/SectionCard";
import { Toggle } from "../components/Toggle";
import type { PlanFormState } from "../types";

interface Props {
  state: PlanFormState;
  setState: Dispatch<SetStateAction<PlanFormState>>;
}

export function Step2Rules({ state, setState }: Props) {
  const { t } = useTranslation();

  const set = <K extends keyof PlanFormState>(key: K, value: PlanFormState[K]) =>
    setState((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">
          {t("charge_plans.step2.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("charge_plans.step2.subtitle")}
        </p>
      </header>

      {/* === 1. Limites de valores para sua cobranca === */}
      <SectionCard
        icon={<MoneyIcon />}
        title={t("charge_plans.step2.value.title")}
        description={t("charge_plans.step2.value.subtitle")}
        required
      >
        {state.tipoValor === "fixo" ? (
          // Modo "Eu defino o valor" — min, max, tipo de incremento, valor do incremento
          <div className="flex flex-col gap-4">
            <InfoCallout variant="tip">
              {state.incrementoHabilitado ? (
                <>
                  <p>{t("charge_plans.step2.value.callout_line1")}</p>
                  <p className="mt-1">
                    {t("charge_plans.step2.value.callout_example", {
                      v1: state.valorMinimo || "300",
                      v2: String(
                        (parseFloat(state.valorMinimo) || 300) +
                          (parseFloat(state.valorStep) || 100),
                      ),
                      v3: String(
                        (parseFloat(state.valorMinimo) || 300) +
                          2 * (parseFloat(state.valorStep) || 100),
                      ),
                    })}
                  </p>
                </>
              ) : (
                <p>
                  {t("charge_plans.step2.value.callout_no_increment", {
                    min: state.valorMinimo || "300",
                  })}
                </p>
              )}
            </InfoCallout>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Field label={t("charge_plans.step1.range.min")}>
                <MoneyInput
                  value={state.valorMinimo}
                  onChange={(v) => set("valorMinimo", v)}
                />
              </Field>
              <Field label={t("charge_plans.step1.range.max")}>
                <MoneyInput
                  value={state.valorMaximo}
                  onChange={(v) => set("valorMaximo", v)}
                />
              </Field>
              <Field label={t("charge_plans.step2.value.increment_toggle_label")}>
                <div className="flex h-11 items-center gap-3 rounded-lg border border-input bg-card px-3">
                  <Toggle
                    checked={state.incrementoHabilitado}
                    onChange={(v) => set("incrementoHabilitado", v)}
                  />
                  <span className="text-sm text-foreground">
                    {state.incrementoHabilitado
                      ? t("charge_plans.step2.value.increment_on")
                      : t("charge_plans.step2.value.increment_off")}
                  </span>
                </div>
              </Field>
              {state.incrementoHabilitado ? (
                <Field label={t("charge_plans.step2.value.increment_amount_label")}>
                  <MoneyInput
                    value={state.valorStep}
                    onChange={(v) => set("valorStep", v)}
                  />
                </Field>
              ) : (
                <div />
              )}
            </div>
          </div>
        ) : (
          // Modo "Cliente escolhe o valor" — apenas min/max, cliente decide no app
          <div className="flex flex-col gap-4">
            <InfoCallout variant="tip">
              {t("charge_plans.step1.range.increment_off_callout", {
                min: state.valorMinimo || "300",
                max: state.valorMaximo || "1000",
              })}
            </InfoCallout>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label={t("charge_plans.step1.range.min")}>
                <MoneyInput
                  value={state.valorMinimo}
                  onChange={(v) => set("valorMinimo", v)}
                />
              </Field>
              <Field label={t("charge_plans.step1.range.max")}>
                <MoneyInput
                  value={state.valorMaximo}
                  onChange={(v) => set("valorMaximo", v)}
                />
              </Field>
            </div>
          </div>
        )}
      </SectionCard>

      {/* === 2. Liberacao de novas cobrancas simultaneas === */}
      <SectionCard
        icon={<RotatingIcon />}
        title={t("charge_plans.step2.simultaneous.title")}
        description={t("charge_plans.step2.simultaneous.subtitle")}
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_320px]">
          <div className="flex flex-col gap-4">
            <Field
              label={t("charge_plans.step2.simultaneous.field_label")}
              hint={t("charge_plans.step2.simultaneous.hint")}
            >
              <div className="max-w-[240px]">
                <NumberInput
                  value={String(state.maxActiveCharges)}
                  onChange={(v) => {
                    const n = Math.min(10, Math.max(1, parseInt(v, 10) || 1));
                    set("maxActiveCharges", n);
                  }}
                  min={1}
                  suffix={t("charge_plans.step2.simultaneous.suffix")}
                />
              </div>
            </Field>
            <InfoCallout>
              {t("charge_plans.step2.simultaneous.callout")}
            </InfoCallout>
          </div>

          <SimultaneousPreview max={state.maxActiveCharges} />
        </div>
      </SectionCard>

      {/*
        Bloco "Dias da semana para cobrar" foi movido para a etapa anterior
        ("Plano de parcelas e prazos", Step3Installments) — faz mais sentido
        ficar perto da definicao das parcelas.
      */}

      {/* === 3. Renovacao automatica === */}
      <SectionCard
        icon={<RefreshIcon />}
        title={t("charge_plans.step2.renewal.title")}
        description={t("charge_plans.step2.renewal.description")}
        rightSlot={
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t("charge_plans.step2.renewal.toggle")}
            </span>
            <Toggle
              checked={state.renovacaoHabilitada}
              onChange={(v) => set("renovacaoHabilitada", v)}
            />
          </div>
        }
      >
        <div
          className={`grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 ${
            state.renovacaoHabilitada ? "" : "opacity-50 pointer-events-none"
          }`}
        >
          <RenewalProgressGate
            progressoTipo={state.renovacaoProgressoTipo}
            onProgressoTipoChange={(v) => set("renovacaoProgressoTipo", v)}
            parcelasValue={state.renovacaoParcelasMinPagas}
            onParcelasChange={(v) => set("renovacaoParcelasMinPagas", v)}
            percentualValue={state.renovacaoPercentualPagoMin}
            onPercentualChange={(v) => set("renovacaoPercentualPagoMin", v)}
          />
          <RenewalGate
            icon="📅"
            title={t("charge_plans.step2.renewal.gate_min_days_title")}
            desc={t("charge_plans.step2.renewal.gate_min_days_desc")}
            value={state.renovacaoDiasMin}
            onChange={(v) => set("renovacaoDiasMin", v)}
            suffix={t("charge_plans.step2.renewal.gate_min_days_suffix")}
          />
          <RenewalGate
            icon="⚠"
            title={t("charge_plans.step2.renewal.gate_max_fines_title")}
            desc={t("charge_plans.step2.renewal.gate_max_fines_desc")}
            value={state.renovacaoMultasMax}
            onChange={(v) => set("renovacaoMultasMax", v)}
            suffix={t("charge_plans.step2.renewal.gate_max_fines_suffix")}
          />
          <RenewalGate
            icon="ℹ"
            title={t("charge_plans.step2.renewal.gate_open_fines_title")}
            desc={t("charge_plans.step2.renewal.gate_open_fines_desc")}
            value={state.renovacaoMultasAbertasMax}
            onChange={(v) => set("renovacaoMultasAbertasMax", v)}
            suffix={t("charge_plans.step2.renewal.gate_open_fines_suffix")}
          />
        </div>
      </SectionCard>
    </div>
  );
}

// ====== Sub-componentes ======

function SimultaneousPreview({ max }: { max: number }) {
  const { t } = useTranslation();
  const reached = max <= 1;
  return (
    <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("charge_plans.step2.simultaneous.preview_label")}
      </p>
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            {t("charge_plans.step2.simultaneous.preview_in_progress")}
          </span>
          <span className="text-xs font-semibold text-primary-700">70%</span>
        </div>
        <div className="text-base font-bold text-foreground">R$ 1.200,00</div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-2 rounded-full bg-primary-500" style={{ width: "70%" }} />
        </div>
        <div className="mt-4">
          {reached ? (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-center text-xs font-medium text-amber-700">
              {t("charge_plans.step2.simultaneous.preview_reached")}
            </div>
          ) : (
            <button
              type="button"
              disabled
              className="w-full cursor-not-allowed rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white opacity-90"
            >
              {t("charge_plans.step2.simultaneous.preview_request")}
            </button>
          )}
        </div>
        <p className="mt-2 text-center text-[10px] text-muted-foreground">
          {t("charge_plans.step2.simultaneous.preview_note", { max })}
        </p>
      </div>
    </div>
  );
}

/**
 * Card de gate "progresso" — gestor escolhe se o cliente precisa atingir
 * um numero minimo de parcelas pagas OU uma porcentagem minima do contrato pago.
 */
function RenewalProgressGate({
  progressoTipo,
  onProgressoTipoChange,
  parcelasValue,
  onParcelasChange,
  percentualValue,
  onPercentualChange,
}: {
  progressoTipo: "parcelas" | "percentual";
  onProgressoTipoChange: (v: "parcelas" | "percentual") => void;
  parcelasValue: string;
  onParcelasChange: (v: string) => void;
  percentualValue: string;
  onPercentualChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col rounded-xl border border-border p-4">
      <div className="mb-3 flex items-start gap-2">
        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs text-primary-600">
          ✓
        </span>
        <h3 className="text-sm font-bold text-foreground">
          {t("charge_plans.step2.renewal.gate_progress_title")}
        </h3>
      </div>
      <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
        {t("charge_plans.step2.renewal.gate_progress_desc")}
      </p>

      {/* Toggle entre modos — selecionado fica verde (primary) para o gestor
          identificar qual regra esta ativa de relance. */}
      <div className="mb-3 inline-flex rounded-lg border border-border bg-muted/30 p-0.5 text-xs">
        <button
          type="button"
          onClick={() => onProgressoTipoChange("parcelas")}
          className={`flex-1 rounded-md px-2 py-1 font-semibold transition ${
            progressoTipo === "parcelas"
              ? "bg-primary-600 text-white shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("charge_plans.step2.renewal.gate_progress_mode_parcelas")}
        </button>
        <button
          type="button"
          onClick={() => onProgressoTipoChange("percentual")}
          className={`flex-1 rounded-md px-2 py-1 font-semibold transition ${
            progressoTipo === "percentual"
              ? "bg-primary-600 text-white shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("charge_plans.step2.renewal.gate_progress_mode_percentual")}
        </button>
      </div>

      {progressoTipo === "parcelas" ? (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <NumberInput value={parcelasValue} onChange={onParcelasChange} />
          </div>
          <span className="text-xs text-muted-foreground">
            {t("charge_plans.step2.renewal.gate_min_paid_suffix")}
          </span>
        </div>
      ) : (
        <div className="flex-1">
          <PercentInput value={percentualValue} onChange={onPercentualChange} />
          <p className="mt-1 text-[10px] leading-tight text-muted-foreground">
            {t("charge_plans.step2.renewal.gate_progress_percent_hint")}
          </p>
        </div>
      )}
    </div>
  );
}

function RenewalGate({
  icon,
  title,
  desc,
  value,
  onChange,
  suffix,
}: {
  icon: string;
  title: string;
  desc: string;
  value: string;
  onChange: (v: string) => void;
  suffix: string;
}) {
  return (
    <div className="flex h-full flex-col rounded-xl border border-border p-4">
      <div className="mb-3 flex items-start gap-2">
        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs text-primary-600">
          {icon}
        </span>
        <h3 className="text-sm font-bold text-foreground">{title}</h3>
      </div>
      <p className="mb-3 flex-1 text-xs leading-relaxed text-muted-foreground">
        {desc}
      </p>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <NumberInput value={value} onChange={onChange} />
        </div>
        <span className="text-xs text-muted-foreground">{suffix}</span>
      </div>
    </div>
  );
}

// ====== Icones ======

function RefreshIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    </svg>
  );
}

function RotatingIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2v6h-6" />
      <path d="M3 22v-6h6" />
      <path d="M3 12a9 9 0 0115-6.7L21 8" />
      <path d="M21 12a9 9 0 01-15 6.7L3 16" />
    </svg>
  );
}

function MoneyIcon() {
  // Sacola com etiqueta — coerente com o mockup de "Limites de valores".
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 7h12l-1 13H7L6 7Z" />
      <path d="M9 7V5a3 3 0 0 1 6 0v2" />
    </svg>
  );
}

